/**
 * HYPSEYA — Therapist Report (public, no auth)
 * Accessible via /therapist-report/:token
 * Shared by the client from the Clinical Report page.
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API          = `${process.env.REACT_APP_BACKEND_URL}/api`;
const wordmarkUrl  = '/hypseya-wordmark.png';

const card = {
  background: '#fff', borderRadius: '18px', padding: '24px 28px',
  marginBottom: '14px', boxShadow: '0 2px 16px rgba(30,107,104,0.07)',
  border: '1px solid rgba(184,211,207,0.25)'
};
const label = { fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9AADAB', marginBottom: '6px' };
const value = { fontSize: '1.1rem', fontWeight: '700', color: '#1E6B68' };

export default function TherapistReport() {
  const { token } = useParams();
  const [report, setReport]   = useState(null);
  const [status, setStatus]   = useState('loading'); // loading | ok | expired | notfound

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/share/report/view/${token}`);
        setReport(res.data);
        setStatus('ok');
      } catch (err) {
        if (err?.response?.status === 410) setStatus('expired');
        else setStatus('notfound');
      }
    };
    if (token) load();
  }, [token]);

  const expiresDate = report?.expires_at
    ? new Date(report.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  if (status === 'loading') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8f7' }}>
      <p style={{ color: '#9AADAB', fontStyle: 'italic' }}>Loading report…</p>
    </div>
  );

  if (status === 'expired') return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f5f8f7', padding: '24px', textAlign: 'center' }}>
      <img src={wordmarkUrl} alt="HYPSEYA" style={{ width: 140, opacity: 0.7, marginBottom: 24 }} />
      <p style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.2rem', color: '#1E6B68', marginBottom: 8 }}>This report link has expired.</p>
      <p style={{ color: '#9AADAB', fontSize: '0.85rem' }}>Report links are valid for 7 days. Ask your client to generate a new one.</p>
    </div>
  );

  if (status === 'notfound') return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f5f8f7', padding: '24px', textAlign: 'center' }}>
      <img src={wordmarkUrl} alt="HYPSEYA" style={{ width: 140, opacity: 0.7, marginBottom: 24 }} />
      <p style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.2rem', color: '#1E6B68', marginBottom: 8 }}>Report not found.</p>
      <p style={{ color: '#9AADAB', fontSize: '0.85rem' }}>This link may be invalid or has already been removed.</p>
    </div>
  );

  const s = report;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg,#F7F3EC 0%,#EFE7DD 60%,rgba(184,211,207,0.18) 100%)', padding: '32px 20px 60px' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <img src={wordmarkUrl} alt="HYPSEYA" style={{ width: 130, opacity: 0.85, marginBottom: 10 }} />
            <p style={{ margin: 0, fontSize: '0.72rem', color: '#9AADAB', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Clinical Progress Report · Shared by client
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, fontSize: '0.72rem', color: '#9AADAB' }}>Period: {s.period_days} days</p>
            <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: '#C5B4A0' }}>Link expires {expiresDate}</p>
            <button onClick={() => window.print()} style={{
              marginTop: 8, padding: '6px 14px', borderRadius: '20px',
              border: '1px solid rgba(30,107,104,0.25)', background: 'none',
              color: '#1E6B68', fontSize: '0.72rem', cursor: 'pointer', fontWeight: '600'
            }}>
              🖨 Print / Save PDF
            </button>
          </div>
        </div>

        {/* Client name */}
        <div style={{ ...card, background: 'linear-gradient(135deg,#1E6B68,#2D8B87)', color: '#fff', marginBottom: 20 }}>
          <p style={{ ...label, color: 'rgba(255,255,255,0.7)' }}>Client</p>
          <p style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.5rem', margin: '0 0 4px', fontWeight: '400' }}>{s.user_name}</p>
          <p style={{ margin: 0, fontSize: '0.82rem', opacity: 0.75 }}>HYPSEYA Emotional Regulation Program · {s.period_days}-day report</p>
        </div>

        {!s.ready ? (
          <div style={card}>
            <p style={{ color: '#9AADAB', textAlign: 'center', fontStyle: 'italic' }}>{s.message || 'Insufficient data for report generation.'}</p>
          </div>
        ) : (<>
          {/* Stats */}
          {s.stats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 14 }}>
              {[
                { l: 'Sessions',       v: s.stats.total_sessions },
                { l: 'ERI Score',      v: s.stats.eri_current ? `${s.stats.eri_current}/100` : '—' },
                { l: 'Avg Regulation Δ', v: s.stats.avg_regulation_delta != null ? `${s.stats.avg_regulation_delta > 0 ? '+' : ''}${s.stats.avg_regulation_delta}` : '—' },
                { l: 'Journal Entries', v: s.stats.journal_entries ?? '—' },
              ].map(({ l, v }) => (
                <div key={l} style={{ ...card, marginBottom: 0, textAlign: 'center' }}>
                  <p style={label}>{l}</p>
                  <p style={value}>{v}</p>
                </div>
              ))}
            </div>
          )}

          {/* Highlights */}
          {s.highlights?.length > 0 && (
            <div style={card}>
              <p style={{ ...label, marginBottom: 14 }}>Key Observations</p>
              {s.highlights.map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <span style={{ color: '#2D8B87', fontSize: '0.9rem', flexShrink: 0 }}>◆</span>
                  <p style={{ margin: 0, fontSize: '0.88rem', color: '#444', lineHeight: '1.6' }}>{h}</p>
                </div>
              ))}
            </div>
          )}

          {/* Narrative */}
          {s.narrative && (
            <div style={card}>
              <p style={{ ...label, marginBottom: 14 }}>Clinical Narrative</p>
              {s.narrative.opening && <p style={{ fontSize: '0.88rem', color: '#444', lineHeight: '1.7', marginBottom: 14 }}>{s.narrative.opening}</p>}
              {['regulation','stability','consistency','integration'].map(k => s.narrative[k] && (
                <div key={k} style={{ marginBottom: 16 }}>
                  <p style={{ ...label, color: '#2D8B87', marginBottom: 4 }}>{k}</p>
                  <p style={{ margin: 0, fontSize: '0.88rem', color: '#444', lineHeight: '1.7' }}>{s.narrative[k]}</p>
                </div>
              ))}
              {s.narrative.recommendation && (
                <div style={{ background: 'rgba(30,107,104,0.04)', borderRadius: 12, padding: '14px 18px', marginTop: 8 }}>
                  <p style={{ ...label, color: '#1E6B68', marginBottom: 6 }}>Recommendation</p>
                  <p style={{ margin: 0, fontSize: '0.88rem', color: '#1E6B68', lineHeight: '1.7' }}>{s.narrative.recommendation}</p>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: 28, padding: '0 20px' }}>
            <p style={{ fontSize: '0.72rem', color: '#B8C8C6', lineHeight: '1.8' }}>
              This report was generated by HYPSEYA and shared by the client.<br />
              It is intended to complement — not replace — clinical assessment.<br />
              HYPSEYA · hypseya.com
            </p>
          </div>
        </>)}
      </div>
    </div>
  );
}
